import { html, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { InputWidthValues } from '../../gzInput';
import { ButtonColor, ButtonFill, ButtonSize, ButtonType, FontSize } from '../../gzIconButton/types';
import { FormValidator, ValidationResult } from '../../gzForm/formField/types/form-validator';
import { translateFn } from '../../../utils/translator.helper';
import { Keys } from '../../../types/common/types';
import { GzInputWebComponent } from '../../gzInput/component/GzInputWebComponent';
import { GzFormFieldWebComponent } from '../../gzForm/formField';
import { DataTableItem } from '../types';
import { FormEvent } from '../../gzForm/formField/types/form-event.enum';
import { DataTableStrings, DefaultColumnKeys, IColumnType, IDataTableColumn } from '../../gzDataTable/types';
import { getDataTableDefaultStrings } from '../../gzDataTable/types/strings';
import { CustomizableValidator } from '../../gzForm/validators/customizable.validator';
import { isEmpty } from 'lodash';

/**
 * @summary This component stores multiple string values, that are given by the user in a single text box one by one,
 * and displayed in a grid under the text box, having the possibility to remove them from an "Action" column
 * @tag gz-input-text-grid
 */
@customElement('gz-input-text-grid')
export class GzInputTextGridWebComponent extends FormFieldInput<string[]> {
  /**
   * The input text box label
   */
  @property({ type: String, attribute: true, reflect: true })
  public label = '';

  /**
   * Whether the field is mandatory. Visually display a star '*' next to the label
   * and add temporary "Required" validators when grid does not contain items.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  public required = false;

  /**
   * The error message displayed under the text box whenever "Required" validators are triggered
   */
  @property({ type: String, attribute: true, reflect: true })
  public requiredErrorMessage = translateFn('Required field');

  /**
   * Whether to allow duplicate input text to be stored in the grid.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  public allowDuplicateItems = false;

  /**
   * The error text displayed under the input box when it contains a value already present in the grid.
   */
  @property({ type: String, attribute: true, reflect: true })
  public duplicateErrorMessage = '';

  /**
   * The input text box placeholder
   */
  @property({ type: String, attribute: true, reflect: true })
  public placeholder = '';

  /**
   * The input text box width
   */
  @property({ type: String, attribute: true, reflect: true })
  public inputWidth = InputWidthValues.XXLARGE;

  /**
   * The number of items allowed to be added to the grid. Default value: 100.
   */
  @property({ type: Number, attribute: true, reflect: true })
  public maxItems = 100;

  /**
   * The text displayed under the input box when the max items limit has been reached.
   */
  @property({ type: String, attribute: true, reflect: true })
  public maxItemsReachedErrorMessage = '';

  /**
   * The name of the first column displayed in the grid header
   */
  @property({ type: String, attribute: true, reflect: true })
  public columnName = '';

  /**
   * The link text displayed in the grid's empty state. When clicked, the input text box will become focused.
   */
  @property({ type: String, attribute: true, reflect: true })
  public emptyStateButtonLinkText = '';

  /**
   * Current state of the list of strings displayed in the grid
   */
  @state()
  private currentItemsList: DataTableItem[] = [];

  /**
   * Current disabled state of the Add button
   */
  @state()
  private addButtonDisabled = true;

  @query('#input-form-field')
  private inputFormFieldComponent: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#input')
  private inputComponent: GzInputWebComponent;

  /**
   * A pair of two unique references to temporary "Required" form validators.
   *
   * These objects are added and removed from the list of Input Validators whenever the grid is emptied
   * and filled with at least 1 item.
   *
   * The first validator is always attached at the beginning of the validators list,
   * while the second validator is always attached at the end of the validators list.
   *
   * The first validator will display the error when both input box and grid are empty.
   * The second validator will display the error when input box passed all other validations and grid is still empty.
   */
  private RequiredValidatorsTemp: [FormValidator, FormValidator];

  static get styles() {
    return styles;
  }

  protected firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);

    this.addEventListener(FormEvent.FormValid, this.onFormValidEvent);
    this.addEventListener(FormEvent.FormInvalid, this.onFormInvalidEvent);

    this.RequiredValidatorsTemp = [
      CustomizableValidator(this.firstRequiredValidator.bind(this)),
      CustomizableValidator(this.lastRequiredValidator.bind(this)),
    ];

    if (isEmpty(this.inputFormFieldComponent.Validators)) {
      this.InputValidators = [];
    }
  }

  public getFormValue(): string[] {
    return this.currentItemsList.map(item => item.value);
  }

  public setFormValue(value: string[]): void {
    this.currentItemsList = [...value.map(item => ({ value: item }))];
  }

  public set InputValidators(value: FormValidator[]) {
    const MaxItemsValidator: FormValidator = CustomizableValidator(this.maxItemsValidator.bind(this));
    const DuplicateItemValidator: FormValidator = CustomizableValidator(this.duplicateItemValidator.bind(this));

    value = [MaxItemsValidator, ...value];

    if (!this.allowDuplicateItems) {
      value = [DuplicateItemValidator, ...value];
    }

    if (this.required && isEmpty(this.currentItemsList)) {
      value = [this.RequiredValidatorsTemp[0], ...value, this.RequiredValidatorsTemp[1]];
    }

    this.inputFormFieldComponent.Validators = value;
  }

  public onFormValidEvent(): void {
    // When empty input, disable Add button
    this.addButtonDisabled = !this.inputFormFieldComponent.value;
  }

  public onFormInvalidEvent(event: CustomEvent<{ error: string | undefined }>): void {
    const isInformativeError = this.required && event.detail.error === this.requiredErrorMessage;
    const isEmptyInput = !this.inputFormFieldComponent.value;

    // When input is required, completed, and "Required field" error is displayed, the Add button should remain enabled
    this.addButtonDisabled = isEmptyInput || !isInformativeError;
  }

  public render() {
    return html`
      <div class="wrapper d-flex flex-column">
        <div class="d-flex flex-row">${this.renderTextInputFormField()} ${this.renderAddButton()}</div>
        ${this.renderGrid()}
      </div>
    `;
  }

  private firstRequiredValidator(): ValidationResult {
    const inputValue = this.inputFormFieldComponent.value;
    const gridHasItems = !isEmpty(this.currentItemsList);

    if (!inputValue && !gridHasItems) {
      // Empty text box and grid does NOT have items. Show "Required field" message
      return { valid: false, error: this.requiredErrorMessage };
    }

    return { valid: true };
  }

  private lastRequiredValidator(): ValidationResult {
    const gridHasItems = !isEmpty(this.currentItemsList);

    return {
      valid: gridHasItems,
      error: gridHasItems ? undefined : this.requiredErrorMessage,
    };
  }

  private maxItemsValidator(value: string): ValidationResult {
    if (!value || this.currentItemsList.length < this.maxItems) {
      return { valid: true };
    }

    return { valid: false, error: this.maxItemsReachedErrorMessage };
  }

  private duplicateItemValidator(value: string): ValidationResult {
    const isDuplicate = this.getFormValue().includes(value);

    if (!value || !isDuplicate) {
      return { valid: true };
    }

    return { valid: false, error: this.duplicateErrorMessage };
  }

  private renderTextInputFormField() {
    return html`
      <gz-form-field id="input-form-field" label="${this.label}" .required="${this.required}">
        <gz-input
          id="input"
          gz-form-input
          label="${this.placeholder}"
          type="text"
          inputWidth="${this.inputWidth}"
          placeholder="${this.placeholder}"
          @keyup="${this.onInputKeyUp}"
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
    `;
  }

  private renderAddButton() {
    return html`
      <gz-icon-button
        id="add-button"
        class="ml-2"
        icon="ds-icon-add"
        ?disabled="${this.addButtonDisabled}"
        fontSize="${FontSize.ICON_SIZE_24}"
        size="${ButtonSize.SIZE_32}"
        buttonFill="${ButtonFill.PRIMARY}"
        buttonType="${ButtonType.SQUARE}"
        buttonColor="${ButtonColor.WHITE}"
        @click="${this.handleAddButtonClick}"
      ></gz-icon-button>
    `;
  }

  private renderGrid() {
    const columns = this.getGridColumnDetails();

    const strings: DataTableStrings = {
      ...getDataTableDefaultStrings(),
      emptyStateFooterButton: this.emptyStateButtonLinkText,
      deleteRowTooltip: '',
    };

    return html`
      <gz-data-table
        id="dataTable"
        .items="${this.currentItemsList}"
        .columns="${columns}"
        allowRowDeletion=${true}
        .strings=${strings}
        @row-deleted=${this.handleDeleteRow}
        @empty-state-footer-button-clicked=${this.onEmptyStateButtonClick}
      ></gz-data-table>
    `;
  }

  private onInputKeyUp(event: KeyboardEvent): void {
    if (event.key === Keys.Enter) {
      this.handleAddButtonClick();
    }
  }

  private handleDeleteRow(e: CustomEvent) {
    this.currentItemsList = this.currentItemsList.filter(item => item !== e.detail.item);

    if (isEmpty(this.currentItemsList) && this.required) {
      this.addRequiredValidators();
    }

    this.emitChangeEvent();
  }

  private handleAddButtonClick() {
    const inputValue = this.inputFormFieldComponent.value;

    if (!inputValue || this.addButtonDisabled) {
      return;
    }

    const itemsLimitReached = this.currentItemsList.length === this.maxItems;
    const alreadyPresent = this.currentItemsList.some(item => item.value === inputValue);

    if (!alreadyPresent && !itemsLimitReached) {
      this.currentItemsList = [...this.currentItemsList, { value: inputValue }];
      this.emitChangeEvent();
    }

    this.inputFormFieldComponent.value = '';

    this.removeRequiredValidators();
  }

  private getGridColumnDetails(): IDataTableColumn[] {
    return [
      {
        key: 'value',
        header: this.columnName,
        columnType: IColumnType.DISPLAY,
      },
      {
        key: DefaultColumnKeys.ACTIONS,
        header: translateFn('Action'),
        columnType: IColumnType.DISPLAY,
      },
    ];
  }

  private onEmptyStateButtonClick(): void {
    this.inputComponent.inputFocus();
  }

  private addRequiredValidators() {
    this.inputFormFieldComponent.Validators = [
      this.RequiredValidatorsTemp[0],
      ...this.inputFormFieldComponent.Validators,
      this.RequiredValidatorsTemp[1],
    ];
  }

  private removeRequiredValidators() {
    this.inputFormFieldComponent.Validators = this.inputFormFieldComponent.Validators.filter(
      (validator: FormValidator<string>): boolean => !this.RequiredValidatorsTemp.includes(validator)
    );
  }

  private emitChangeEvent(): void {
    const event = new CustomEvent('change', {
      detail: {
        value: this.getFormValue(),
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-input-text-grid': GzInputTextGridWebComponent;
  }
}
